/**
 * Utilitários para traduzir o status do job considerando a task atual
 * (atributo `task` retornado pela API a partir do backend mais recente).
 *
 * O job pode conter etapas (tasks) dinâmicas com seu próprio status interno
 * (WAITING, RUNNING, ERROR, etc). O job reflete esse status para simplificar a
 * visualização na listagem. Aqui combinamos `task` + `status` para apresentar
 * rótulos amigáveis específicos para certas etapas do fluxo (ex.: revisão de
 * contestação ou processamento de transcrições) sem precisar criar
 * "traduções" dedicadas no backend a cada nova combinação.
 */

/**
 * Status em que uma task de revisão/aprovação realmente depende de ação humana.
 *
 * - RUNNING        : nas tasks de revisão isso significa "aguardando o
 *                    revisor concluir" (não é o sistema executando).
 * - PENDING_APPROVAL: status legado enviado pelo backend antes da migração
 *                    para `task` + `status` originais.
 *
 * O status `WAITING` para uma task de revisão indica que o sistema ainda
 * está finalizando o conteúdo a ser revisado (ex.: "Finalizando
 * transcrições"); por isso não conta como ação humana.
 */
const HUMAN_ACTION_STATUSES = ["RUNNING", "PENDING_APPROVAL"];

/**
 * Devolve o rótulo amigável de status considerando a task em andamento.
 *
 * Ordem de resolução:
 *   1. `status.task.{TASK}.{STATUS}` no namespace `jobs` (se houver tradução).
 *   2. Compatibilidade: status legado `PENDING_APPROVAL` é mapeado para
 *      `CONTESTATION_REVIEW.RUNNING` (até o backend parar de enviar esse
 *      status já traduzido).
 *   3. Fallback para `status.{STATUS}` (rótulo padrão por status).
 *
 * @param {string} status status atual do job (ou da task que o job está refletindo)
 * @param {object} job   linha completa do job (precisa de `task` quando disponível)
 * @param {function} t   função `t` do `useTranslation("jobs")`
 * @returns {string}
 */
export function getJobStatusLabel(status, job, t) {
  if (!t) return status || "";

  const task = job?.task;
  const upperStatus = status ? String(status).toUpperCase() : "";

  if (task && upperStatus) {
    const taskKey = `status.task.${task}.${upperStatus}`;
    const translated = t(taskKey);
    if (translated && translated !== taskKey) {
      return translated;
    }
  }

  /* Compatibilidade enquanto o backend ainda enviar PENDING_APPROVAL no lugar
     de task=CONTESTATION_REVIEW + status=RUNNING. Nas listagens de criação de
     documento isso sempre representa a revisão da contestação. */
  if (upperStatus === "PENDING_APPROVAL") {
    const legacyKey = "status.task.CONTESTATION_REVIEW.RUNNING";
    const legacyTranslated = t(legacyKey);
    if (legacyTranslated && legacyTranslated !== legacyKey) {
      return legacyTranslated;
    }
  }

  if (!upperStatus) return "";
  const fallbackKey = `status.${upperStatus}`;
  const fallback = t(fallbackKey);
  return fallback && fallback !== fallbackKey ? fallback : status;
}

/**
 * Indica se o job está aguardando a revisão de transcrições de áudio/vídeo
 * (task=TRANSCRIPTION_REVIEW em estado que demanda intervenção do revisor).
 *
 * Usado para condicionar a exibição do botão de ação dedicado na listagem.
 *
 * @param {object} job
 * @returns {boolean}
 */
export function isAwaitingTranscriptionReview(job) {
  if (!job || job.task !== "TRANSCRIPTION_REVIEW") return false;
  const status = job.status ? String(job.status).toUpperCase() : "";
  return HUMAN_ACTION_STATUSES.includes(status);
}

/**
 * Indica se o job está aguardando a revisão da contestação. Reconhece tanto o
 * estado novo (task=CONTESTATION_REVIEW + status RUNNING) quanto o legado
 * (status=PENDING_APPROVAL) para manter o botão de revisão funcionando
 * durante a transição do backend.
 *
 * @param {object} job
 * @returns {boolean}
 */
export function isAwaitingContestationReview(job) {
  if (!job) return false;
  const status = job.status ? String(job.status).toUpperCase() : "";
  if (status === "PENDING_APPROVAL") return true;
  if (job.task === "CONTESTATION_REVIEW") {
    return HUMAN_ACTION_STATUSES.includes(status);
  }
  return false;
}

/**
 * Indica se o status atual do job depende de uma ação humana
 * (revisão/aprovação) e portanto deve receber destaque visual na listagem.
 *
 * Centraliza a regra para que novas etapas de revisão/aprovação possam ser
 * incluídas em um único ponto à medida que forem adicionadas no backend.
 *
 * @param {object} job
 * @returns {boolean}
 */
export function requiresHumanAction(job) {
  return (
    isAwaitingContestationReview(job) || isAwaitingTranscriptionReview(job)
  );
}
